import { Box, Heading, IconButton, Stack, Text, type ToastId, useToast } from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { type EffectCallback, useCallback, useEffect, useRef } from 'react'

import logger from '@/utils/logger'

import RPC from '@/client/rpc'
import { ACHIEVEMENT_ID_TO_DATA, type AchievementId } from '@/client/services/progression'

import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Icon from '@/components/UI/Icon/Icon'
import useUndisplayedUserAchievements from '@/components/Worlds/hooks/user/useUndisplayedUserAchievements'

const TOAST_DURATION_MS = 7 * 1000

function UndisplayedAchievementToastBody({
  achievementId,
  onCloseToast,
}: {
  achievementId: AchievementId
  onCloseToast: () => void
}): JSX.Element {
  const { title, subtitle, iconActive, link } = ACHIEVEMENT_ID_TO_DATA[achievementId]

  return (
    <Stack
      direction="row"
      align="center"
      spacing="16px"
      bg="bg"
      borderRadius="8px"
      border="2px solid"
      borderColor="border.emphasized"
      width={{ base: '330px', sm: '480px' }}
      p="16px"
      boxShadow="md"
    >
      <PublicImage src={`/progression/${iconActive}`} width={{ base: '50px', sm: '80px' }} />
      <Stack direction="column" spacing="8px" flex={1}>
        <Stack direction="row" spacing="8px" width="100%" justify="space-between" align="center">
          <Text variant="eyebrow">Achievement Unlocked</Text>
          <IconButton
            variant="icon"
            size="sm"
            icon={<Icon size="sm" faIcon={faClose} />}
            onClick={onCloseToast}
            aria-label="Dismiss toast"
          />
        </Stack>
        <Heading size="lg" mb="8px">
          {title}
        </Heading>
        {link == null ? (
          <Text fontSize="14px">{subtitle}</Text>
        ) : (
          <Box mb={4}>
            <ShwLink href={link}>{subtitle}</ShwLink>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

function UndisplayedAchievementToast({
  achievementId,
}: {
  achievementId: AchievementId
}): JSX.Element {
  const toast = useToast()
  const toastIdRef = useRef<ToastId>()

  const onCloseToast = useCallback(() => {
    if (toastIdRef.current == null) {
      return
    }
    toast.close(toastIdRef.current)
  }, [toast])

  useEffect((): ReturnType<EffectCallback> => {
    if (toastIdRef.current != null) {
      return
    }

    const toastId = toast({
      position: 'bottom-right',
      render: () => (
        <UndisplayedAchievementToastBody
          achievementId={achievementId}
          onCloseToast={onCloseToast}
        />
      ),
      duration: TOAST_DURATION_MS,
      isClosable: true,
    })

    toastIdRef.current = toastId

    RPC.acknowledgeAchievement(
      {
        achievementId,
      },
      {
        disableProgressBar: true,
      }
    ).catch(err => {
      logger.warning('Error acknowledging badge', { err })
    })
  }, [achievementId, toast, onCloseToast])

  return <Box />
}

export default function UndisplayedAchievementToasts(): JSX.Element {
  const { achievementIds } = useUndisplayedUserAchievements()

  return (
    <>
      {achievementIds.map(achievementId => (
        <UndisplayedAchievementToast achievementId={achievementId} key={achievementId} />
      ))}
    </>
  )
}
